import React from "react";
import CurrencyInput from "./CurrencyInput";
import NeighborhoodSelect from "./NeighborhoodSelect";
import BuildingSelect from "./BuildingSelect";
import MembersEditor from "./MembersEditor";
import { BOROUGHS } from "../config/investorOnboardingSchema";
import { COUNTRIES, US_STATES } from "../config/geo";

/**
 * Renders a single onboarding field by its schema `type`. Controlled: `value` comes
 * from the page's form state, `onChange(newValue)` writes it back (raw value, not event).
 *
 * `neighborhoods` ({id,name,borough,region}[]) and `teamMembers` ({id,name}[]) are
 * injected by the page for the `neighborhoods` and `users` field types.
 */

const labelStyle = { display: "block", fontSize: "13px", fontWeight: 600, color: "#333", marginBottom: "6px" };
const reqStar = { color: "#c0392b", marginLeft: "3px" };
const helpStyle = { fontSize: "11.5px", color: "#8a929b", marginTop: "5px", lineHeight: 1.4 };
const inputStyle = {
  width: "100%", padding: "9px 10px", border: "1.5px solid #dfe3e8", borderRadius: "6px",
  fontSize: "14px", boxSizing: "border-box", backgroundColor: "white",
};
const selectStyle = { ...inputStyle, cursor: "pointer" };

// Options may be plain strings or { value, label }. Normalize to { value, label } so the
// DB stores a stable `value` code while the UI shows `label` (TODO3). String options
// keep value === label (backward compatible).
const normOpts = (options = []) =>
  options.map((o) => (o !== null && typeof o === "object" ? o : { value: o, label: o }));

function Chip({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "6px 12px", borderRadius: "20px", fontSize: "13px", cursor: "pointer",
        border: active ? "1.5px solid #1976d2" : "1.5px solid #dfe3e8",
        backgroundColor: active ? "#e3f2fd" : "white",
        color: active ? "#1565c0" : "#4a5058", fontWeight: active ? 600 : 500,
        transition: "all .12s ease",
      }}
    >
      {active ? "✓ " : ""}{label}
    </button>
  );
}

export default function InvestorOnboardingField({ field, value, onChange, neighborhoods = [], teamMembers = [], form = {} }) {
  const { type, label, required, options = [], placeholder, help } = field;

  const renderControl = () => {
    switch (type) {
      case "text":
      case "email":
      case "tel":
        return (
          <input
            type={type === "text" ? "text" : type}
            value={value ?? ""}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
            style={inputStyle}
          />
        );

      case "textarea":
        return (
          <textarea
            value={value ?? ""}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
            rows={2}
            style={{ ...inputStyle, resize: "vertical", minHeight: "40px", fontFamily: "inherit" }}
          />
        );

      case "date":
        return (
          <input type="date" value={value ?? ""} onChange={(e) => onChange(e.target.value)} style={inputStyle} />
        );

      case "number":
        return (
          <input
            type="number"
            value={value ?? ""}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
            style={inputStyle}
          />
        );

      case "currency":
        return <CurrencyInput value={value ?? ""} onChange={onChange} style={inputStyle} />;

      case "percent":
        return (
          <div style={{ position: "relative" }}>
            <input
              type="number"
              value={value ?? ""}
              placeholder={placeholder}
              min={0}
              max={100}
              onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
              style={{ ...inputStyle, paddingRight: "28px" }}
            />
            <span style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", color: "#8a929b", fontSize: "14px" }}>%</span>
          </div>
        );

      case "select":
        return (
          <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} style={selectStyle}>
            <option value="">— Select —</option>
            {normOpts(options).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        );

      case "users":
        // Fall back to free text when the team-member list isn't available (e.g. non-admin).
        if (!teamMembers.length) {
          return (
            <input type="text" value={value ?? ""} placeholder="Team member name"
              onChange={(e) => onChange(e.target.value)} style={inputStyle} />
          );
        }
        return (
          <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} style={selectStyle}>
            <option value="">— Select —</option>
            {teamMembers.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        );

      case "multiselect": {
        const arr = Array.isArray(value) ? value : [];
        const toggle = (v) => onChange(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "7px" }}>
            {normOpts(options).map((o) => (
              <Chip key={o.value} label={o.label} active={arr.includes(o.value)} onClick={() => toggle(o.value)} />
            ))}
          </div>
        );
      }

      case "boroughs": {
        const arr = Array.isArray(value) ? value : [];
        const toggle = (o) => onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "7px" }}>
            {BOROUGHS.map((o) => (
              <Chip key={o} label={o} active={arr.includes(o)} onClick={() => toggle(o)} />
            ))}
          </div>
        );
      }

      case "neighborhoods":
        return (
          <NeighborhoodSelect
            neighborhoods={neighborhoods}
            selected={Array.isArray(value) ? value : []}
            onChange={onChange}
            multi
            label={null}
            placeholder="Search neighborhoods…"
          />
        );

      case "buildings":
        return (
          <BuildingSelect
            selected={Array.isArray(value) ? value : []}
            onChange={onChange}
            label={null}
            accent={field.accent || "neutral"}
            placeholder={placeholder || "Search buildings…"}
          />
        );

      case "country":
        return (
          <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} style={selectStyle}>
            <option value="">— Select country —</option>
            {COUNTRIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        );

      case "usstate": {
        // Only a US-state dropdown when the sibling country is the US; otherwise free text.
        const isUS = form[field.dependsOn] === "United States";
        if (!isUS) {
          return (
            <input type="text" value={value ?? ""} placeholder="State / Province"
              onChange={(e) => onChange(e.target.value)} style={inputStyle} />
          );
        }
        return (
          <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} style={selectStyle}>
            <option value="">— Select state —</option>
            {US_STATES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        );
      }

      case "members":
        return <MembersEditor value={Array.isArray(value) ? value : []} onChange={onChange} />;

      case "computed":
        return (
          <div style={{
            ...inputStyle, backgroundColor: "#f6f7f9", color: "#9aa3ad", fontStyle: "italic",
            display: "flex", alignItems: "center", cursor: "not-allowed",
          }}>
            {value != null && value !== "" ? String(value) : "Computed automatically"}
          </div>
        );

      default:
        return <div style={{ color: "#c0392b", fontSize: "13px" }}>Unknown field type: {type}</div>;
    }
  };

  return (
    <div style={{ marginBottom: "2px" }}>
      <label style={labelStyle}>
        {label}
        {required && <span style={reqStar}>*</span>}
      </label>
      {renderControl()}
      {help && <div style={helpStyle}>{help}</div>}
    </div>
  );
}
